/** @jsxImportSource @emotion/react */

import { useTheme } from '@emotion/react';
import { Fragment, MouseEvent } from 'react';

import { ProgressState } from '../App';
import { data, Progress } from '../Data/index';
import { ReactComponent as ArrowLeft } from './arrow-left.svg';
import { ReactComponent as ArrowRight } from './arrow-right.svg';
import { ReactComponent as RetakeArrow } from './retake-arrow.svg';

type ProgressBarProps = {
  onNextButtonClick: (event?: MouseEvent<HTMLButtonElement>) => void;
  onPreviousButtonClick: (event?: MouseEvent<HTMLButtonElement>) => void;
  progress: ProgressState;
};

export const ProgressBar = ({ onNextButtonClick, onPreviousButtonClick, progress }: ProgressBarProps) => {
  const theme = useTheme();

  const text =
    progress.status !== Progress.Complete
      ? data.dictionary.progress.replace('{count}', progress.currentQuestion.toString())
      : data.dictionary.progress_complete;

  // When scrolled to the Submit Panel, the question count becomes "8"
  // Hide the progress indicator dots when the quiz is completed/submitted OR when the question count > the actual number of questions.
  const progressIndicatorHidden = progress.status == Progress.Complete || progress.currentQuestion > data.questions.length;

  // Hide if quiz is completed/submitted OR when viewing first question.
  const backArrowHidden = progress.status === Progress.Complete || progress.currentQuestion === 1;

  // Hide if quiz is completed/submitted OR when viewing the last question.
  const forwardArrowHidden = progress.status === Progress.Complete || progress.currentQuestion === 8;

  // Hide unless the quiz is completed/submitted.
  const retakeQuizButtonHidden = progress.status !== Progress.Complete;

  const backArrow = (
    <button
      aria-label={data.dictionary.previous}
      css={{
        backgroundColor: 'transparent',
        border: 0,
        color: theme.color.primary,
        cursor: 'pointer',

        '&:focus:not(:focus-visible)': {
          outline: 'none'
        }
      }}
      onClick={onPreviousButtonClick}
      type="button"
      disabled={backArrowHidden}
      hidden={backArrowHidden}
    >
      <ArrowLeft
        css={{
          display: 'block',
          height: '32px',
          transition: 'fill 100ms ease-in',
          width: '32px',

          '&:focus, &:hover': {
            fill: theme.color.black
          },

          [`@media (min-width: 640px)`]: {
            height: '48px',
            width: '48px'
          }
        }}
      />
    </button>
  );

  const forwardArrow = (
    <button
      aria-label={data.dictionary.next}
      css={{
        backgroundColor: 'transparent',
        border: 0,
        color: theme.color.primary,
        cursor: 'pointer',

        '&:focus:not(:focus-visible)': {
          outline: 'none'
        }
      }}
      disabled={forwardArrowHidden}
      hidden={forwardArrowHidden}
      onClick={onNextButtonClick}
      type="button"
    >
      <ArrowRight
        css={{
          display: 'block',
          height: '32px',
          transition: 'fill 100ms ease-in',
          width: '32px',

          '&:focus, &:hover': {
            fill: theme.color.black
          },

          [`@media (min-width: 640px)`]: {
            height: '48px',
            width: '48px'
          }
        }}
      />
    </button>
  );

  const retakeQuizButton = (
    <button
      css={{
        alignItems: 'center',
        background: 'transparent',
        border: 'none',
        boxSizing: 'border-box',
        color: theme.color.primary,
        cursor: 'pointer',
        display: retakeQuizButtonHidden ? 'none' : 'flex',
        fontFamily: theme.fontFamily.body,
        fontSize: theme.fontSizes[2],
        fontWeight: theme.fontWeights.semibold,
        padding: '0',
        textTransform: 'uppercase',
        transition: 'color 100ms ease-in',

        '&:focus, &:hover': {
          color: theme.color.black,

          '& svg': {
            fill: theme.color.black
          }
        },

        '&:focus:not(:focus-visible)': {
          outline: 'none'
        },

        [`@media (min-width: 640px)`]: {
          fontSize: theme.fontSizes[4]
        }
      }}
      disabled={retakeQuizButtonHidden}
      hidden={retakeQuizButtonHidden}
      type="reset"
    >
      <RetakeArrow
        css={{
          fill: theme.color.primary,
          height: '1rem',
          marginRight: '.5rem',
          transition: 'fill .1s ease-in',
          width: '1rem',

          [`@media (min-width: 640px)`]: {
            height: '1.25em',
            marginRight: '1rem',
            width: '1.25rem'
          }
        }}
      />
      {data.dictionary.retake}
    </button>
  );
  return (
    <div
      css={{
        background: theme.color.white,
        bottom: '0',
        boxShadow: '0px -4px 1rem rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        left: '0',
        minHeight: '86px',
        padding: '1rem',
        position: 'fixed',
        right: '0',
        transform: 'translate3d(0, 100%, 0)',
        transition: 'transform 250ms ease-in-out',
        zIndex: 2,

        [`@media (min-width: 640px)`]: {
          minHeight: '80px'
        }
      }}
      style={{
        transform: progress.currentQuestion > 0 ? 'none' : undefined
      }}
    >
      <div
        css={{
          alignItems: 'center',
          display: progress.status !== Progress.Complete ? 'flex' : 'none',
          justifyContent: 'flex-start',
          textAlign: 'left',
          width: 'calc(100% / 3)',

          [`@media (min-width: 640px)`]: {
            display: 'flex'
          }
        }}
      >
        {backArrow}
      </div>

      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >
        {!progressIndicatorHidden && (
          <Fragment>
            <div
              css={{
                fontSize: theme.fontSizes[2]
              }}
            >
              {text}
            </div>

            <div
              css={{
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'flex',
                marginBottom: 6,
                marginTop: 16
              }}
              role="presentation"
            >
              {data.questions.map((q, i) => (
                <div
                  css={{
                    backgroundColor: theme.color.softGrey,
                    borderRadius: '50%',
                    height: 8,
                    marginRight: 8,
                    width: 8,

                    '&:last-child': {
                      marginRight: 0
                    }
                  }}
                  key={`progress-index-${i}`}
                  role="presentation"
                  style={{
                    backgroundColor: progress.currentQuestion - 1 === i ? theme.color.primary : undefined
                  }}
                />
              ))}
            </div>
          </Fragment>
        )}
      </div>

      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-end',
          textAlign: 'right',
          width: 'calc(100% / 3)'
        }}
      >
        {forwardArrow}
      </div>

      {progress.status === Progress.Complete && (
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%'
          }}
        >
          {retakeQuizButton}
        </div>
      )}
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';
